import { supabase } from '../lib/supabase';

export const adminService = {
  /**
   * Check if current user is an admin employee
   */
  async isAdminEmployee() {
    try {
      const { data, error } = await supabase?.rpc('is_admin_employee');
      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  },

  /**
   * Get current admin employee profile
   */
  async getAdminProfile() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return null;

      const { data, error } = await supabase?.from('admin_employees')?.select(`
        *,
        user:user_profiles!admin_employees_user_id_fkey (
          id,
          email,
          full_name,
          avatar_url
        )
      `)?.eq('user_id', user?.id)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        userId: data?.user_id,
        employeeId: data?.employee_id,
        role: data?.admin_role,
        department: data?.department,
        isActive: data?.is_active,
        twoFactorEnabled: data?.two_factor_enabled,
        lastLoginAt: data?.last_login_at,
        lastLoginIp: data?.last_login_ip,
        failedLoginAttempts: data?.failed_login_attempts,
        accountLockedUntil: data?.account_locked_until,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at,
        user: data?.user ? {
          id: data?.user?.id,
          email: data?.user?.email,
          fullName: data?.user?.full_name,
          avatarUrl: data?.user?.avatar_url
        } : null
      };
    } catch (error) {
      console.error('Error fetching admin profile:', error);
      throw error;
    }
  },

  /**
   * Get all users with filtering and search
   */
  async getAllUsers(filters = {}) {
    try {
      let query = supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });

      if (filters?.role) {
        query = query?.eq('role', filters?.role);
      }

      if (filters?.accountStatus === 'locked') {
        query = query?.eq('account_locked', true);
      } else if (filters?.accountStatus === 'active') {
        query = query?.eq('account_locked', false);
      }

      if (filters?.emailVerified !== undefined) {
        query = query?.eq('email_verified', filters?.emailVerified);
      }

      if (filters?.search) {
        query = query?.or(`full_name.ilike.%${filters?.search}%,email.ilike.%${filters?.search}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(user => ({
        id: user?.id,
        email: user?.email,
        fullName: user?.full_name,
        role: user?.role,
        avatarUrl: user?.avatar_url,
        emailVerified: user?.email_verified,
        accountLocked: user?.account_locked,
        accountLockedAt: user?.account_locked_at,
        createdAt: user?.created_at,
        updatedAt: user?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  /**
   * Get platform statistics
   */
  async getPlatformStatistics() {
    try {
      const { data, error } = await supabase?.from('platform_statistics')?.select('*');

      if (error) throw error;

      const stats = {};
      data?.forEach(stat => {
        stats[stat.metric_name] = stat?.metric_value;
      });

      return stats;
    } catch (error) {
      console.error('Error fetching platform statistics:', error);
      throw error;
    }
  },

  /**
   * Update platform statistics (admin only)
   */
  async updatePlatformStatistics() {
    try {
      const { error } = await supabase?.rpc('update_platform_statistics');
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error updating platform statistics:', error);
      throw error;
    }
  },

  /**
   * Suspend user account
   */
  async suspendUser(userId, reason) {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({
          account_locked: true,
          account_locked_at: new Date()?.toISOString(),
          lockout_reason: reason
        })?.eq('id', userId);

      if (error) throw error;

      // Log admin action
      await this.logAdminAction('user_suspend', userId, null, null, null, reason);

      return true;
    } catch (error) {
      console.error('Error suspending user:', error);
      throw error;
    }
  },

  /**
   * Activate user account
   */
  async activateUser(userId, reason) {
    try {
      const { error } = await supabase?.rpc('unlock_account', {
        target_user_id: userId,
        admin_note: reason
      });

      if (error) throw error;

      // Log admin action
      await this.logAdminAction('user_activate', userId, null, null, null, reason);

      return true;
    } catch (error) {
      console.error('Error activating user:', error);
      throw error;
    }
  },

  /**
   * Manually verify user email (admin override)
   */
  async verifyUserEmail(userId, reason) {
    try {
      const { data: adminProfile } = await this.getAdminProfile();
      if (!adminProfile) throw new Error('Admin profile not found');

      const { error } = await supabase?.rpc('admin_verify_email', {
        target_user_id: userId,
        admin_user_id: adminProfile?.id,
        reason_text: reason
      });

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error verifying user email:', error);
      throw error;
    }
  },

  /**
   * Change user role
   */
  async changeUserRole(userId, newRole, reason) {
    try {
      const { error } = await supabase?.from('user_profiles')?.update({ role: newRole })?.eq('id', userId);

      if (error) throw error;

      // Log admin action
      await this.logAdminAction(
        'role_change',
        userId,
        null,
        null,
        { new_role: newRole },
        reason
      );

      return true;
    } catch (error) {
      console.error('Error changing user role:', error);
      throw error;
    }
  },

  /**
   * Get admin activity logs
   */
  async getAdminActivityLogs(filters = {}) {
    try {
      let query = supabase?.from('admin_activity_log')?.select(`
          *,
          admin:admin_employees!admin_activity_log_admin_id_fkey (
            id,
            full_name,
            email,
            role
          ),
          target_user:user_profiles!admin_activity_log_target_user_id_fkey (
            id,
            full_name,
            email
          )
        `)?.order('performed_at', { ascending: false })?.limit(100);

      if (filters?.actionType) {
        query = query?.eq('action_type', filters?.actionType);
      }

      if (filters?.adminId) {
        query = query?.eq('admin_id', filters?.adminId);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(log => ({
        id: log?.id,
        actionType: log?.action_type,
        targetUserId: log?.target_user_id,
        targetEntityType: log?.target_entity_type,
        targetEntityId: log?.target_entity_id,
        actionDetails: log?.action_details,
        reason: log?.reason,
        performedAt: log?.performed_at,
        admin: log?.admin ? {
          id: log?.admin?.id,
          fullName: log?.admin?.full_name,
          email: log?.admin?.email,
          role: log?.admin?.role
        } : null,
        targetUser: log?.target_user ? {
          id: log?.target_user?.id,
          fullName: log?.target_user?.full_name,
          email: log?.target_user?.email
        } : null
      }));
    } catch (error) {
      console.error('Error fetching admin activity logs:', error);
      throw error;
    }
  },

  /**
   * Log admin action
   */
  async logAdminAction(
    actionType,
    targetUserId = null,
    targetEntityType = null,
    targetEntityId = null,
    actionDetails = null,
    reason = null
  ) {
    try {
      const { data, error } = await supabase?.rpc('log_admin_action', {
        p_action_type: actionType,
        p_target_user_id: targetUserId,
        p_target_entity_type: targetEntityType,
        p_target_entity_id: targetEntityId,
        p_action_details: actionDetails,
        p_reason: reason
      });

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error logging admin action:', error);
      throw error;
    }
  },

  /**
   * Get user account flags
   */
  async getUserAccountFlags(userId) {
    try {
      const { data, error } = await supabase?.from('user_account_flags')?.select(`
          *,
          flagged_by_admin:admin_employees!user_account_flags_flagged_by_fkey (
            full_name,
            email
          ),
          resolved_by_admin:admin_employees!user_account_flags_resolved_by_fkey (
            full_name,
            email
          )
        `)?.eq('user_id', userId)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(flag => ({
        id: flag?.id,
        userId: flag?.user_id,
        flagType: flag?.flag_type,
        flagReason: flag?.flag_reason,
        resolved: flag?.resolved,
        resolvedAt: flag?.resolved_at,
        createdAt: flag?.created_at,
        flaggedBy: flag?.flagged_by_admin,
        resolvedBy: flag?.resolved_by_admin
      }));
    } catch (error) {
      console.error('Error fetching user account flags:', error);
      throw error;
    }
  },

  /**
   * Create user account flag
   */
  async createUserAccountFlag(userId, flagType, flagReason) {
    try {
      const { data: adminProfile } = await this.getAdminProfile();
      if (!adminProfile) throw new Error('Admin profile not found');

      const { data, error } = await supabase?.from('user_account_flags')?.insert({
          user_id: userId,
          flag_type: flagType,
          flag_reason: flagReason,
          flagged_by: adminProfile?.id
        })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating user account flag:', error);
      throw error;
    }
  },

  /**
   * Resolve user account flag
   */
  async resolveUserAccountFlag(flagId) {
    try {
      const { data: adminProfile } = await this.getAdminProfile();
      if (!adminProfile) throw new Error('Admin profile not found');

      const { error } = await supabase?.from('user_account_flags')?.update({
          resolved: true,
          resolved_by: adminProfile?.id,
          resolved_at: new Date()?.toISOString()
        })?.eq('id', flagId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error resolving user account flag:', error);
      throw error;
    }
  },

  /**
   * Assign subscription plan to a company (admin only)
   */
  async assignSubscriptionPlan(companyId, planType, assignedBy) {
    try {
      // Validate plan type
      const validPlans = ['free', 'freemium', 'standard', 'premium'];
      if (!validPlans?.includes(planType)) {
        throw new Error(`Invalid plan type: ${planType}`);
      }

      // Update company subscription
      const { data, error } = await supabase
        ?.from('company_subscriptions')
        ?.update({
          plan: planType,
          status: 'active',
          updated_at: new Date()?.toISOString()
        })
        ?.eq('company_id', companyId)
        ?.select()
        ?.single();

      if (error) throw error;

      // Log admin action
      await supabase?.from('admin_activity_logs')?.insert({
        admin_id: assignedBy,
        action: 'assign_subscription_plan',
        target_type: 'company',
        target_id: companyId,
        details: {
          plan_type: planType,
          previous_plan: data?.plan,
          assigned_at: new Date()?.toISOString()
        }
      });

      return {
        success: true,
        data
      };
    } catch (error) {
      console.error('Error assigning subscription plan:', error);
      return {
        success: false,
        error: error?.message
      };
    }
  }
};

export default adminService;